// ...existing code...
import { generateInvoicePDF } from "./InvoicePDF";
const logo = "logo.png";
import * as React from "react";
// VAT default rate
const DEFAULT_VAT_RATE = 0.2;
import { useState, useMemo } from 'react';
import { Search, TrendingUp, Package, ShoppingCart, Receipt, Calendar, Plus, X, FileText, Download } from 'lucide-react';

// Move Extra type above Sale type
type Extra = { name: string; price: number };
type Vehicle = {
  id: number;
  make: string;
  model: string;
  year: number;
  stock: number;
  price: number;
  costPrice: number;
  additionalCosts: number;
  status: string;
  vin: string;
  mileage: number;
  notes?: string;
  [key: string]: any;
};
type Expense = {
  id: number;
  date: string;
  category: string;
  description: string;
  amount: number;
  vat?: number;
  vehicleId?: number | null;
  linkedToStock?: boolean;
  invoiceFileUrl?: string; // URL or base64 for uploaded invoice
};
type Sale = {
  id: number;
  invoiceNumber: string;
  vehicleId: number;
  vehicle: string;
  customer: string;
  customerEmail: string;
  customerPhone: string;
  customerAddress: string;
  salePrice: number;
  date: string;
  salesPerson: string;
  documentationFee: number;
  registrationFee: number;
  extras: Extra[];
  tradeInDescription: string;
  tradeInValue: number;
  discountNote: string;
  discountValue: number;
  paymentMethod: string;
  deposit: number;
  useFinance: boolean;
  apr: number;
  termMonths: number;
  notes: string;
  terms: string;
  [key: string]: any;
};

// Dealer type definition
type Dealer = {
  id: number;
  name: string;
  businessName: string;
  address: string;
  phone: string;
  email: string;
  website?: string;
  licenseNumber?: string;
  vatNumber?: string;
  logo?: string;
};

const VehicleStockApp = () => {
  // Dealer management state
  const [dealers, setDealers] = useState<Dealer[]>(() => loadLS('dealers', [
    {
      id: 1,
      name: "Default Dealer",
      businessName: "Car Sales Business",
      address: "123 Main Street, City, State 12345",
      phone: "(555) 123-4567",
      email: "sales@carsales.com",
      website: "www.carsales.com",
      licenseNumber: "DL123456",
      vatNumber: "VAT123456789"
    }
  ]));
  const [currentDealerId, setCurrentDealerId] = useState(() => loadLS('currentDealerId', 1));
  const [showDealerModal, setShowDealerModal] = useState(false);

  // Current dealer computed value
  const currentDealer = dealers.find(d => d.id === currentDealerId) || dealers[0];

  // ...existing code...
  // VAT registration state
  const [vatRegistered, setVatRegistered] = useState(() => loadLS('vatRegistered', false));
  const [vatRate, setVatRate] = useState(() => loadLS('vatRate', DEFAULT_VAT_RATE));
  React.useEffect(() => { saveLS('vatRegistered', vatRegistered); }, [vatRegistered]);
  React.useEffect(() => { saveLS('vatRate', vatRate); }, [vatRate]);
  
  // Dealer state persistence
  React.useEffect(() => { saveLS('dealers', dealers); }, [dealers]);
  React.useEffect(() => { saveLS('currentDealerId', currentDealerId); }, [currentDealerId]);
  
  const [activeTab, setActiveTab] = useState('inventory');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMake, setFilterMake] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('make');
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(false);
  type Vehicle = {
    id: number;
    make: string;
    model: string;
    year: number;
    stock: number;
    price: number;
    costPrice: number;
    additionalCosts: number;
    status: string;
    vin: string;
    mileage: number;
    notes?: string;
    [key: string]: any;
  }

  type Sale = {
    id: number;
    invoiceNumber: string;
    vehicleId: number;
    vehicle: string;
    customer: string;
    customerEmail: string;
    customerPhone: string;
    customerAddress: string;
    salePrice: number;
    date: string;
    salesPerson: string;
    documentationFee: number;
    registrationFee: number;
    extras: Extra[];
    tradeInDescription: string;
    tradeInValue: number;
    discountNote: string;
    discountValue: number;
    paymentMethod: string;
    deposit: number;
    useFinance: boolean;
    apr: number;
    termMonths: number;
    notes: string;
    terms: string;
    [key: string]: any;
  };
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);

  // LocalStorage helpers
  function loadLS(key: string, fallback: any) {
    try {
      const val = localStorage.getItem(key);
      return val ? JSON.parse(val) : fallback;
    } catch {
      return fallback;
    }
  }
  function saveLS(key: string, val: any) {
    localStorage.setItem(key, JSON.stringify(val));
  }

  // Dealer-specific storage functions
  function loadDealerLS(key: string, fallback: any, dealerId: number) {
    const dealerKey = `dealer_${dealerId}_${key}`;
    return loadLS(dealerKey, fallback);
  }
  function saveDealerLS(key: string, val: any, dealerId: number) {
    const dealerKey = `dealer_${dealerId}_${key}`;
    saveLS(dealerKey, val);
  }

  // Function to switch between dealers
  function switchToDealer(dealerId: number) {
    // Save current dealer's data before switching
    saveDealerLS('vehicles', vehicles, currentDealerId);
    saveDealerLS('sales', sales, currentDealerId);
    saveDealerLS('expenses', expenses, currentDealerId);
    
    // Load new dealer's data
    const newVehicles = loadDealerLS('vehicles', [], dealerId);
    const newSales = loadDealerLS('sales', [], dealerId);
    const newExpenses = loadDealerLS('expenses', [], dealerId);
    
    // Update state
    setVehicles(newVehicles);
    setSales(newSales);
    setExpenses(newExpenses);
    setCurrentDealerId(dealerId);
  }

  // Initialize with current dealer data
  const currentDealerIdValue = loadLS('currentDealerId', 1);
  
  const [vehicles, setVehicles] = useState(() => {
    // Try to load dealer-specific data first, fallback to legacy data
    const dealerVehicles = loadDealerLS('vehicles', null, currentDealerIdValue);
    if (dealerVehicles) return dealerVehicles;
    
    // Migrate legacy data to dealer-specific storage
    const legacyVehicles = loadLS('vehicles', [
      { id: 1, make: 'Toyota', model: 'Camry', year: 2023, stock: 12, price: 28500, costPrice: 25000, additionalCosts: 0, status: 'in-stock', vin: 'JT2BF28K5X0123456', mileage: 12000 },
      { id: 2, make: 'Honda', model: 'Civic', year: 2024, stock: 8, price: 24900, costPrice: 22000, additionalCosts: 0, status: 'in-stock', vin: 'JHMFC36278X012345', mileage: 8000 },
      { id: 3, make: 'Ford', model: 'F-150', year: 2023, stock: 15, price: 42000, costPrice: 38000, additionalCosts: 0, status: 'in-stock', vin: '1FTEW1EP8MFA12345', mileage: 15000 },
      { id: 4, make: 'Tesla', model: 'Model 3', year: 2024, stock: 3, price: 45000, costPrice: 41000, additionalCosts: 0, status: 'low-stock', vin: '5YJ3E1EA9LF012345', mileage: 5000 },
      { id: 5, make: 'BMW', model: '3 Series', year: 2023, stock: 6, price: 48900, costPrice: 44000, additionalCosts: 0, status: 'in-stock', vin: 'WBA8E5C52NA012345', mileage: 10000 }
    ]);
    
    // Save to dealer-specific storage and clear legacy
    saveDealerLS('vehicles', legacyVehicles, currentDealerIdValue);
    localStorage.removeItem('vehicles');
    return legacyVehicles;
  });
  const [sales, setSales] = useState<Sale[]>(() => {
    // Try to load dealer-specific data first, fallback to legacy data
    const dealerSales = loadDealerLS('sales', null, currentDealerIdValue);
    if (dealerSales) return dealerSales;
    
    // Migrate legacy data to dealer-specific storage
    const legacySales = loadLS('sales', [
    {
      id: 1,
      invoiceNumber: 'INV-2025-001',
      vehicleId: 1,
      vehicle: 'Toyota Camry 2023',
      customer: 'John Smith',
      customerEmail: 'john.smith@email.com',
      customerPhone: '07700 900123',
      customerAddress: '123 High Street, London, SW1A 1AA',
      salePrice: 29500,
      date: '2025-09-15',
      salesPerson: 'Mike Johnson',
      documentationFee: 99,
      registrationFee: 55,
      extras: [{ name: '12-Month Warranty', price: 295 }, { name: 'Floor Mats', price: 35 }],
      tradeInDescription: '',
      tradeInValue: 0,
      discountNote: '',
      discountValue: 0,
      paymentMethod: 'Bank Transfer',
      deposit: 0,
      useFinance: false,
      apr: 0,
      termMonths: 0,
      notes: '',
      terms: `TERMS & CONDITIONS OF SALE\n\n1. Definitions\nSeller means HNS Car Sales Ltd. Customer/Buyer means the person purchasing the vehicle. Vehicle means the used motor vehicle described in this invoice.\n\n2. Condition of Vehicle\nThe Vehicle is sold as a used vehicle; age-related wear is to be expected. No representation is made beyond information provided in good faith unless expressly stated in writing.\n\n3. Inspection & Test Drive\nCustomer has had the opportunity to inspect/test-drive and accepts the Vehicle's condition.\n\n4. Price & Payment\nDeposit (if any) is non-refundable unless the Seller cannot supply the Vehicle. Full cleared funds required before release.\n\n5. Title & Risk\nTitle passes on receipt of full cleared payment. Risk passes on collection/delivery.\n\n6. Collection & Delivery\nUnless agreed in writing, Customer collects from Seller's premises. Any delivery is at Customer's cost and risk.\n\n7. Warranty\nWhere a warranty is provided, its duration/scope/exclusions are in a separate schedule. Warranty Claim – Customer Responsibility: in the event of a warranty claim, the Customer is responsible for transporting the Vehicle to the Seller's designated yard/service location for inspection/repair; all transport costs/risks are borne by the Customer unless otherwise agreed in writing. Normal wear/consumables/misuse/accident damage excluded.\n\n8. Part-Exchange (if applicable)\nCustomer warrants full title and disclosure of known defects; allowance may be adjusted if undisclosed issues are found.\n\n9. Finance\nCustomer is responsible for complying with any finance agreement terms.\n\n10. Limitation of Liability\nNothing limits liability for death/personal injury due to negligence or fraud. Seller not liable for indirect/consequential losses (loss of profit/downtime).\n\n11. Data Protection\nCustomer data processed to complete the sale and provide after-sales support in accordance with applicable law.\n\n12. Governing Law\nThese terms are governed by the laws of England & Wales and subject to the exclusive jurisdiction of the English courts.`
    }
  ]);
    
    // Save to dealer-specific storage and clear legacy
    saveDealerLS('sales', legacySales, currentDealerIdValue);
    localStorage.removeItem('sales');
    return legacySales;
  });
  const [expenses, setExpenses] = useState<Expense[]>(() => {
    // Try to load dealer-specific data first, fallback to legacy data
    const dealerExpenses = loadDealerLS('expenses', null, currentDealerIdValue);
    if (dealerExpenses) return dealerExpenses;
    
    // Migrate legacy data to dealer-specific storage
    const legacyExpenses = loadLS('expenses', [
      { id: 1, category: 'Rent', amount: 8500, date: '2025-09-01', description: 'Monthly showroom rent', linkedToStock: false, vehicleId: null },
      { id: 2, category: 'Utilities', amount: 1200, date: '2025-09-05', description: 'Electricity and water', linkedToStock: false, vehicleId: null }
    ]);
    
    // Save to dealer-specific storage and clear legacy
    saveDealerLS('expenses', legacyExpenses, currentDealerIdValue);
    localStorage.removeItem('expenses');
    return legacyExpenses;
  });

  // Persist to dealer-specific localStorage on change
  React.useEffect(() => { saveDealerLS('vehicles', vehicles, currentDealerId); }, [vehicles, currentDealerId]);
  React.useEffect(() => { saveDealerLS('sales', sales, currentDealerId); }, [sales, currentDealerId]);
  React.useEffect(() => { saveDealerLS('expenses', expenses, currentDealerId); }, [expenses, currentDealerId]);

  const [newVehicle, setNewVehicle] = useState({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    stock: 1,
    price: '',
    costPrice: '',
    vin: '',
    mileage: ''
  });

  const [newSale, setNewSale] = useState({
    vehicleId: '',
    customer: '',
    customerEmail: '',
    customerPhone: '',
    customerAddress: '',
    salePrice: '',
    date: new Date().toISOString().split('T')[0],
    salesPerson: '',
    documentationFee: 99,
    registrationFee: 55,
    extras: [{ name: '12-Month Warranty', price: 295 }, { name: 'Floor Mats', price: 35 }],
    tradeInDescription: '',
    tradeInValue: 0,
    discountNote: '',
    discountValue: 0,
    paymentMethod: 'Bank Transfer',
    deposit: 0,
    useFinance: false,
    apr: 0,
    termMonths: 0,
    notes: '',
    terms: `TERMS & CONDITIONS OF SALE\n\n1. Definitions\nSeller means HNS Car Sales Ltd. Customer/Buyer means the person purchasing the vehicle. Vehicle means the used motor vehicle described in this invoice.\n\n2. Condition of Vehicle\nThe Vehicle is sold as a used vehicle; age-related wear is to be expected. No representation is made beyond information provided in good faith unless expressly stated in writing.\n\n3. Inspection & Test Drive\nCustomer has had the opportunity to inspect/test-drive and accepts the Vehicle's condition.\n\n4. Price & Payment\nDeposit (if any) is non-refundable unless the Seller cannot supply the Vehicle. Full cleared funds required before release.\n\n5. Title & Risk\nTitle passes on receipt of full cleared payment. Risk passes on collection/delivery.\n\n6. Collection & Delivery\nUnless agreed in writing, Customer collects from Seller's premises. Any delivery is at Customer's cost and risk.\n\n7. Warranty\nWhere a warranty is provided, its duration/scope/exclusions are in a separate schedule. Warranty Claim – Customer Responsibility: in the event of a warranty claim, the Customer is responsible for transporting the Vehicle to the Seller's designated yard/service location for inspection/repair; all transport costs/risks are borne by the Customer unless otherwise agreed in writing. Normal wear/consumables/misuse/accident damage excluded.\n\n8. Part-Exchange (if applicable)\nCustomer warrants full title and disclosure of known defects; allowance may be adjusted if undisclosed issues are found.\n\n9. Finance\nCustomer is responsible for complying with any finance agreement terms.\n\n10. Limitation of Liability\nNothing limits liability for death/personal injury due to negligence or fraud. Seller not liable for indirect/consequential losses (loss of profit/downtime).\n\n11. Data Protection\nCustomer data processed to complete the sale and provide after-sales support in accordance with applicable law.\n\n12. Governing Law\nThese terms are governed by the laws of England & Wales and subject to the exclusive jurisdiction of the English courts.`,
    vat: '', // VAT amount for sale
  });

  const [newExpense, setNewExpense] = useState({
  category: 'Rent',
  amount: '', // keep as string for input compatibility
  date: new Date().toISOString().split('T')[0],
  description: '',
  linkedToStock: false,
  vehicleId: '',
  vat: '', // VAT amount for expense
  invoiceFileUrl: ''
  });

  const makes = ['all', ...Array.from(new Set(vehicles.map((v: any) => v.make))) as string[]];
  const generalExpenseCategories = ['Rent', 'Utilities', 'Salaries', 'Marketing', 'Insurance', 'Supplies', 'Other'];
  const stockExpenseCategories = ['Fuel', 'Travel', 'Parts', 'Paint', 'Labour', 'Repairs', 'Maintenance', 'Advert'];

  const generateInvoiceNumber = () => {
    const year = new Date().getFullYear();
    const nextNum = sales.length + 1;
    return 'INV-' + year + '-' + String(nextNum).padStart(3, '0');
  };

  const handleAddVehicle = () => {
    if (!newVehicle.make || !newVehicle.model || !newVehicle.year || !newVehicle.price || !newVehicle.costPrice || !newVehicle.vin || !newVehicle.mileage) {
      alert('Please fill in all fields');
      return;
    }

    const stock = Number(newVehicle.stock);
    let status = 'in-stock';
    if (stock === 0) status = 'out-of-stock';
    else if (stock <= 3) status = 'low-stock';

    const vehicle = {
      id: vehicles.length + 1,
      make: newVehicle.make,
      model: newVehicle.model,
      year: Number(newVehicle.year),
      stock: stock,
      price: parseFloat(newVehicle.price),
      costPrice: parseFloat(newVehicle.costPrice),
      additionalCosts: 0,
      status: status,
      vin: newVehicle.vin,
      mileage: parseInt(newVehicle.mileage)
    };
    setVehicles([...vehicles, vehicle]);
    setShowInventoryModal(false);
    setNewVehicle({
      make: '',
      model: '',
      year: new Date().getFullYear(),
      stock: 1,
      price: '',
      costPrice: '',
      vin: '',
      mileage: ''
    });
  };

  const handleAddSale = () => {
    // Basic validation
    if (!newSale.vehicleId) return alert('Please select a vehicle');
    if (!newSale.customer) return alert('Please enter customer name');
    if (!newSale.customerEmail) return alert('Please enter customer email');
    if (!newSale.customerPhone) return alert('Please enter customer phone');
    if (!newSale.customerAddress) return alert('Please enter customer address');
    if (!newSale.salePrice) return alert('Please enter sale price');
    if (!newSale.salesPerson) return alert('Please enter sales person name');

  const vehicle = vehicles.find((v: any) => v.id === parseInt(newSale.vehicleId));
    if (!vehicle) return alert('Vehicle not found');

    // Calculate VAT if enabled
    let saleVAT = 0;
    if (vatRegistered) {
      saleVAT = newSale.vat ? parseFloat(newSale.vat as string) : ((parseFloat(newSale.salePrice as string || '0') + Number(newSale.documentationFee) + Number(newSale.registrationFee)) * vatRate);
    }
    const sale = {
      id: sales.length + 1,
      invoiceNumber: generateInvoiceNumber(),
      vehicleId: parseInt(newSale.vehicleId),
      vehicle: vehicle.make + ' ' + vehicle.model + ' ' + vehicle.year,
      customer: newSale.customer,
      customerEmail: newSale.customerEmail,
      customerPhone: newSale.customerPhone,
      customerAddress: newSale.customerAddress,
      salePrice: parseFloat(newSale.salePrice),
      vat: saleVAT,
      date: newSale.date,
      salesPerson: newSale.salesPerson,
      documentationFee: newSale.documentationFee,
      registrationFee: newSale.registrationFee,
      extras: newSale.extras,
      tradeInDescription: newSale.tradeInDescription,
      tradeInValue: newSale.tradeInValue,
      discountNote: newSale.discountNote,
      discountValue: newSale.discountValue,
      paymentMethod: newSale.paymentMethod,
      deposit: newSale.deposit,
      useFinance: newSale.useFinance,
      apr: newSale.apr,
      termMonths: newSale.termMonths,
      notes: newSale.notes,
      terms: newSale.terms
    };
  setSales([...sales, sale]);

    // Decrement vehicle stock
  setVehicles(vehicles.map((v: any) => {
      if (v.id === parseInt(newSale.vehicleId)) {
        const newStock = v.stock - 1;
        return {
          ...v,
          stock: newStock,
          status: newStock === 0 ? 'out-of-stock' : v.status
        };
      }
      return v;
    }));

    setShowSaleModal(false);
    setNewSale({
      vehicleId: '',
      customer: '',
      customerEmail: '',
      customerPhone: '',
      customerAddress: '',
      salePrice: '',
      date: new Date().toISOString().split('T')[0],
      salesPerson: '',
      documentationFee: 99,
      registrationFee: 55,
      extras: [{ name: '12-Month Warranty', price: 295 }, { name: 'Floor Mats', price: 35 }],
      tradeInDescription: '',
      tradeInValue: 0,
      discountNote: '',
      discountValue: 0,
      paymentMethod: 'Bank Transfer',
      deposit: 0,
      useFinance: false,
      apr: 0,
      termMonths: 0,
      notes: '',
      terms: '',
      vat: ''
    });
    alert('Sale added successfully!');
  };

  const handleAddExpense = () => {
    if (!newExpense.category || !newExpense.amount || !newExpense.description) {
      alert('Please fill in all fields');
      return;
    }
    
    if (newExpense.linkedToStock && !newExpense.vehicleId) {
      alert('Please select a vehicle for stock-related expenses');
      return;
    }

    // Calculate VAT if enabled
    let expenseVAT = 0;
    if (vatRegistered) {
      expenseVAT = newExpense.vat ? parseFloat(newExpense.vat as string) : (parseFloat(newExpense.amount as string || '0') * vatRate);
    }
    const expense = {
      id: expenses.length + 1,
      category: newExpense.category,
      amount: parseFloat(newExpense.amount),
      vat: expenseVAT,
      date: newExpense.date,
      description: newExpense.description,
      linkedToStock: newExpense.linkedToStock,
      vehicleId: newExpense.linkedToStock ? parseInt(newExpense.vehicleId) : null,
      invoiceFileUrl: newExpense.invoiceFileUrl || ''
    };
    
    setExpenses([...expenses, expense]);

    if (newExpense.linkedToStock && newExpense.vehicleId) {
  setVehicles(vehicles.map((v: any) => {
        if (v.id === parseInt(newExpense.vehicleId)) {
          return {
            ...v,
            additionalCosts: v.additionalCosts + parseFloat(newExpense.amount)
          };
        }
        return v;
      }));
    }

    setShowExpenseModal(false);
    setNewExpense({
      category: 'Rent',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      description: '',
      linkedToStock: false,
      vehicleId: '',
      vat: '',
      invoiceFileUrl: ''
    });
  };

  const viewInvoice = (sale: Sale) => {
    setSelectedSale(sale);
    setShowInvoiceModal(true);
  };

  const filteredVehicles = useMemo(() => {
    let filtered = vehicles;

    if (searchTerm) {
      filtered = filtered.filter((v: any) =>
        v.make.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.vin.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterMake !== 'all') {
      filtered = filtered.filter((v: any) => v.make === filterMake);
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter((v: any) => v.status === filterStatus);
    }

    return filtered.sort((a: any, b: any) => {
      if (sortBy === 'make') return a.make.localeCompare(b.make);
      if (sortBy === 'price') return a.price - b.price;
      if (sortBy === 'stock') return b.stock - a.stock;
      return 0;
    });
  }, [searchTerm, filterMake, filterStatus, sortBy, vehicles]);

  const totalStock = vehicles.reduce((sum: number, v: any) => sum + v.stock, 0);

  const getStatusColor = (status: string) => {
    if (status === 'in-stock') return 'bg-green-100 text-green-800';
    if (status === 'low-stock') return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getStatusDot = (status: string) => {
    if (status === 'in-stock') return 'bg-green-500';
    if (status === 'low-stock') return 'bg-yellow-500';
    return 'bg-red-500';
  };

  // Date range state and helpers
  const [reportStartDate, setReportStartDate] = useState<string>("");
  const [reportEndDate, setReportEndDate] = useState<string>("");

  function isInRange(dateStr: string) {
    if (!reportStartDate && !reportEndDate) return true;
    const d = new Date(dateStr);
    if (reportStartDate && d < new Date(reportStartDate)) return false;
    if (reportEndDate && d > new Date(reportEndDate)) return false;
    return true;
  }

  // Filtered data for summary

  const filteredSales = sales.filter(s => isInRange(s.date));
  const filteredExpenses = expenses.filter(e => isInRange(e.date));
  const totalSales = filteredSales.reduce((sum, s) => sum + (s.salePrice || 0), 0);
  const totalExpenses = filteredExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  // Calculate cost of vehicles sold in the filtered sales
  const costOfVehiclesSold = filteredSales.reduce((sum, s) => {
    const v = vehicles.find((veh: any) => veh.id === s.vehicleId);
    return sum + (v ? (v.costPrice || 0) : 0);
  }, 0);
  const profit = totalSales - costOfVehiclesSold - totalExpenses;
  const totalSalesVAT = filteredSales.reduce((sum, s) => sum + (s.vat ? Number(s.vat) : 0), 0);
  const totalExpensesVAT = filteredExpenses.reduce((sum, e) => sum + (e.vat ? Number(e.vat) : 0), 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* VAT Registration Toggle */}
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-4">
            <img
              src={currentDealer.logo || logo}
              alt={`${currentDealer.businessName} Logo`}
              style={{ maxHeight: '64px', width: 'auto', display: 'block' }}
              onError={e => {
                e.currentTarget.style.display = 'none';
                if (e.currentTarget.parentNode) {
                  const msg = document.createElement('div');
                  msg.textContent = 'Logo not found';
                  msg.style.color = 'red';
                  msg.style.fontWeight = 'bold';
                  msg.style.fontSize = '16px';
                  e.currentTarget.parentNode.appendChild(msg);
                }
              }}
            />
          </div>
          {/* VAT Dealer Toggle */}
          <label className="flex items-center gap-2 ml-8">
            <input
              type="checkbox"
              checked={vatRegistered}
              onChange={e => setVatRegistered(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">VAT Registered Dealer</span>
          </label>
          
          {/* Dealer Selector */}
          <div className="flex items-center gap-2 ml-8">
            <label className="text-sm font-medium text-gray-700">Current Dealer:</label>
            <select
              value={currentDealerId}
              onChange={e => switchToDealer(Number(e.target.value))}
              className="px-3 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              {dealers.map(dealer => (
                <option key={dealer.id} value={dealer.id}>
                  {dealer.name}
                </option>
              ))}
            </select>
            <button
              onClick={() => setShowDealerModal(true)}
              className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              Manage Dealers
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Date Range Selector */}
        <div className="flex flex-wrap items-end gap-4 mb-6">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={reportStartDate}
              onChange={e => setReportStartDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={reportEndDate}
              onChange={e => setReportEndDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          {(reportStartDate || reportEndDate) && (
            <button
              onClick={() => { setReportStartDate(""); setReportEndDate(""); }}
              className="px-3 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium"
            >
              Clear
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          {/* Cost of Vehicles Sold */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Cost of Vehicles Sold</p>
                <p className="text-3xl font-bold text-orange-600 mt-1">£{(costOfVehiclesSold / 1000).toFixed(1)}K</p>
              </div>
              <Package className="text-orange-600" size={32} />
            </div>
          </div>
          {/* VAT Cards */}
          {vatRegistered && (
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium">VAT on Sales</p>
                  <p className="text-2xl font-bold text-blue-600 mt-1">£{totalSalesVAT.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                  <p className="text-gray-500 text-sm font-medium mt-2">VAT on Expenses</p>
                  <p className="text-2xl font-bold text-red-600 mt-1">£{totalExpensesVAT.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                </div>
                <FileText className="text-blue-600" size={32} />
              </div>
            </div>
          )}
          {/* Total Units */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Total Units</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{totalStock}</p>
              </div>
              <Package className="text-blue-600" size={32} />
            </div>
          </div>
          {/* Total Sales */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Total Sales</p>
                <p className="text-3xl font-bold text-green-600 mt-1">£{(totalSales / 1000).toFixed(0)}K</p>
              </div>
              <ShoppingCart className="text-green-600" size={32} />
            </div>
          </div>
          {/* Expenses */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Expenses</p>
                <p className="text-3xl font-bold text-red-600 mt-1">£{(totalExpenses / 1000).toFixed(1)}K</p>
              </div>
              <Receipt className="text-red-600" size={32} />
            </div>
          </div>
          {/* Net Profit */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Net Profit</p>
                <p className={'text-3xl font-bold mt-1 ' + (profit >= 0 ? 'text-green-600' : 'text-red-600')}>£{(profit / 1000).toFixed(1)}K</p>
              </div>
              <TrendingUp className={profit >= 0 ? 'text-green-600' : 'text-red-600'} size={32} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab('inventory')}
                className={'px-6 py-3 font-medium ' + (activeTab === 'inventory' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700')}
              >
                <div className="flex items-center gap-2">
                  <Package size={18} />
                  Inventory
                </div>
              </button>
              <button
                onClick={() => setActiveTab('sales')}
                className={'px-6 py-3 font-medium ' + (activeTab === 'sales' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700')}
              >
                <div className="flex items-center gap-2">
                  <ShoppingCart size={18} />
                  Sales
                </div>
              </button>
              <button
                onClick={() => setActiveTab('expenses')}
                className={'px-6 py-3 font-medium ' + (activeTab === 'expenses' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700')}
              >
                <div className="flex items-center gap-2">
                  <Receipt size={18} />
                  Expenses
                </div>
              </button>
            </div>
          </div>

          {activeTab === 'inventory' && (
            <div>
              <div className="p-6 border-b border-gray-200">
                <div className="flex flex-col md:flex-row gap-4 mb-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="text"
                      placeholder="Search by make, model, or VIN..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <select
                    value={filterMake}
                    onChange={(e) => setFilterMake(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {makes.map((make: string) => (
                      <option key={make} value={make}>
                        {make === 'all' ? 'All Makes' : make}
                      </option>
                    ))}
                  </select>

                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Status</option>
                    <option value="in-stock">In Stock</option>
                    <option value="low-stock">Low Stock</option>
                    <option value="out-of-stock">Out of Stock</option>
                  </select>

                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="make">Sort by Make</option>
                    <option value="price">Sort by Price</option>
                    <option value="stock">Sort by Stock</option>
                  </select>
                </div>
                <button 
                  onClick={() => setShowInventoryModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Plus size={18} />
                  Add Vehicle
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vehicle</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">VRN</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Year</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mileage</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sale Price</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Add Costs</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredVehicles.map((vehicle: any) => (
                      <tr key={vehicle.id} className="hover:bg-gray-50 transition">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{vehicle.make}</div>
                              <div className="text-sm text-gray-500">{vehicle.model}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500 font-mono">{vehicle.vin}</div> {/* VRN value, field name unchanged for data compatibility */}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{vehicle.year}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{vehicle.mileage.toLocaleString()} mi</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            £{vehicle.price.toLocaleString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-600">
                            £{vehicle.costPrice.toLocaleString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-orange-600">
                            £{vehicle.additionalCosts.toLocaleString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-bold text-gray-900">{vehicle.stock} units</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ' + getStatusColor(vehicle.status)}>
                            <span className={'w-2 h-2 rounded-full mr-1.5 ' + getStatusDot(vehicle.status)}></span>
                            {vehicle.status.replace('-', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            className="px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-xs"
                            onClick={() => {
                              if (window.confirm('Remove this vehicle from inventory?')) {
                                setVehicles(vehicles.filter((v: any) => v.id !== vehicle.id));
                              }
                            }}
                          >Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'sales' && (
            <div>
              <div className="p-6 border-b border-gray-200 flex flex-col gap-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-gray-900">Recent Sales</h2>
                  <button 
                    onClick={() => setShowSaleModal(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <Plus size={18} />
                    Add Sale
                  </button>
                </div>
                {/* Date Range Selector for Sales */}
                <div className="flex flex-wrap items-end gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={reportStartDate}
                      onChange={e => setReportStartDate(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                    <input
                      type="date"
                      value={reportEndDate}
                      onChange={e => setReportEndDate(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  {(reportStartDate || reportEndDate) && (
                    <button
                      onClick={() => { setReportStartDate(""); setReportEndDate(""); }}
                      className="px-3 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium"
                    >
                      Clear
                    </button>
                  )}
                </div>
                {/* Sales Summary & Export */}
                <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                  <div className="bg-gray-50 p-4 rounded-lg shadow flex flex-col gap-1">
                    <span className="text-sm text-gray-500">Total Sales</span>
                    <span className="text-2xl font-bold text-green-600">£{sales.reduce((sum, s) => sum + (s.salePrice || 0), 0).toLocaleString()}</span>
                    <span className="text-sm text-gray-500">Total Invoices</span>
                    <span className="text-lg font-bold text-blue-600">{sales.length}</span>
                  </div>
                  <button
                    onClick={() => {
                      const csvRows = [
                        ['Invoice','Date','Vehicle','Customer','Sales Person','Sale Price'],
                        ...filteredSales.map(s => [
                          s.invoiceNumber,
                          new Date(s.date).toLocaleDateString('en-GB'),
                          s.vehicle,
                          s.customer,
                          s.salesPerson,
                          s.salePrice
                        ])
                      ];
                      const csvContent = 'data:text/csv;charset=utf-8,' + csvRows.map(e => e.join(',')).join('\n');
                      const encodedUri = encodeURI(csvContent);
                      const link = document.createElement('a');
                      link.setAttribute('href', encodedUri);
                      link.setAttribute('download', 'sales_report.csv');
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                  >
                    <FileText size={18} />
                    Export to CSV
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vehicle</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sales Person</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sale Price</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sales.map((sale) => (
                      <tr key={sale.id} className="hover:bg-gray-50 transition">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-mono text-blue-600">{sale.invoiceNumber}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-900">
                            <Calendar size={16} className="mr-2 text-gray-400" />
                            {new Date(sale.date).toLocaleDateString('en-GB')}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{sale.vehicle}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{sale.customer}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{sale.salesPerson}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-bold text-green-600">
                            £{sale.salePrice.toLocaleString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap flex gap-2">
                          <button
                            onClick={() => viewInvoice(sale)}
                            className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          >
                            <FileText size={16} />
                            <span className="text-sm">View Invoice</span>
                          </button>
                          <button
                            className="px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-xs"
                            onClick={() => {
                              if (window.confirm('Remove this sale?')) {
                                setSales(sales.filter(s => s.id !== sale.id));
                              }
                            }}
                          >Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'expenses' && (
            <div>
              <div className="p-6 border-b border-gray-200 flex flex-col gap-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-gray-900">Business Expenses</h2>
                  <button 
                    onClick={() => setShowExpenseModal(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <Plus size={18} />
                    Add Expense
                  </button>
                </div>
                {/* Date Range Selector for Expenses */}
                <div className="flex flex-wrap items-end gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={reportStartDate}
                      onChange={e => setReportStartDate(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                    <input
                      type="date"
                      value={reportEndDate}
                      onChange={e => setReportEndDate(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  {(reportStartDate || reportEndDate) && (
                    <button
                      onClick={() => { setReportStartDate(""); setReportEndDate(""); }}
                      className="px-3 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium"
                    >
                      Clear
                    </button>
                  )}
                </div>
                {/* Expenses Summary & Export */}
                <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                  <div className="bg-gray-50 p-4 rounded-lg shadow flex flex-col gap-1">
                    <span className="text-sm text-gray-500">Total Expenses</span>
                    <span className="text-2xl font-bold text-red-600">£{expenses.reduce((sum, e) => sum + (e.amount || 0), 0).toLocaleString()}</span>
                    <span className="text-sm text-gray-500">Total Records</span>
                    <span className="text-lg font-bold text-blue-600">{expenses.length}</span>
                  </div>
                  <button
                    onClick={() => {
                      const csvRows = [
                        ['Date','Category','Description','Linked Vehicle','Amount','VAT'],
                        ...filteredExpenses.map(e => [
                          new Date(e.date).toLocaleDateString('en-GB'),
                          e.category,
                          e.description,
                          e.vehicleId ? (vehicles.find((v: Vehicle) => v.id === e.vehicleId)?.make + ' ' + vehicles.find((v: Vehicle) => v.id === e.vehicleId)?.model) : 'General',
                          e.amount,
                          e.vat || ''
                        ])
                      ];
                      const csvContent = 'data:text/csv;charset=utf-8,' + csvRows.map(e => e.join(',')).join('\n');
                      const encodedUri = encodeURI(csvContent);
                      const link = document.createElement('a');
                      link.setAttribute('href', encodedUri);
                      link.setAttribute('download', 'expenses_report.csv');
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                  >
                    <FileText size={18} />
                    Export to CSV
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Linked Vehicle</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {expenses.map((expense) => {
                      const linkedVehicle = expense.vehicleId ? vehicles.find((v: any) => v.id === expense.vehicleId) : null;
                      return (
                        <tr key={expense.id} className="hover:bg-gray-50 transition">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-900">
                              <Calendar size={16} className="mr-2 text-gray-400" />
                              {new Date(expense.date).toLocaleDateString('en-GB')}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ' + (expense.linkedToStock ? 'bg-orange-100 text-orange-800' : 'bg-purple-100 text-purple-800')}>
                              {expense.category}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">{expense.description}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {linkedVehicle ? (
                              <div className="text-sm text-gray-900">
                                {linkedVehicle.make} {linkedVehicle.model}
                              </div>
                            ) : (
                              <div className="text-sm text-gray-400">General</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-bold text-red-600">
                              £{expense.amount.toLocaleString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {expense.invoiceFileUrl ? (
                              (() => {
                                const isImage = expense.invoiceFileUrl.startsWith('data:image/');
                                const isPDF = expense.invoiceFileUrl.startsWith('data:application/pdf');
                                return (
                                  <div className="flex flex-col gap-1">
                                    {isImage && (
                                      <a href={expense.invoiceFileUrl} target="_blank" rel="noopener noreferrer">
                                        <img src={expense.invoiceFileUrl} alt="Invoice" style={{ maxWidth: 60, maxHeight: 60, borderRadius: 4, border: '1px solid #ddd' }} />
                                      </a>
                                    )}
                                    {isPDF && (
                                      <a href={expense.invoiceFileUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">View PDF</a>
                                    )}
                                    <a href={expense.invoiceFileUrl} download className="text-blue-500 underline text-xs">Download</a>
                                  </div>
                                );
                              })()
                            ) : (
                              <span className="text-gray-400 text-xs">No file</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              className="px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-xs"
                              onClick={() => {
                                if (window.confirm('Remove this expense?')) {
                                  setExpenses(expenses.filter(e => e.id !== expense.id));
                                }
                              }}
                            >Remove</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="p-6 bg-gray-50 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-900">Total Expenses</span>
                  <span className="text-2xl font-bold text-red-600">£{totalExpenses.toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showInventoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Add New Vehicle</h3>
              <button onClick={() => setShowInventoryModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Make</label>
                <input
                  type="text"
                  value={newVehicle.make}
                  onChange={(e) => setNewVehicle({...newVehicle, make: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Toyota"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                <input
                  type="text"
                  value={newVehicle.model}
                  onChange={(e) => setNewVehicle({...newVehicle, model: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Camry"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                <input
                  type="number"
                  value={newVehicle.year}
                  onChange={(e) => setNewVehicle({...newVehicle, year: Number(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="2024"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">VIN</label>
                <input
                  type="text"
                  value={newVehicle.vin}
                  onChange={(e) => setNewVehicle({...newVehicle, vin: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="1HGBH41JXMN109186"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cost Price (Purchase)</label>
                <input
                  type="number"
                  value={newVehicle.costPrice}
                  onChange={(e) => setNewVehicle({...newVehicle, costPrice: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="25000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sale Price</label>
                <input
                  type="number"
                  value={newVehicle.price}
                  onChange={(e) => setNewVehicle({...newVehicle, price: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="28500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stock Quantity</label>
                <input
                  type="number"
                  value={newVehicle.stock}
                  onChange={(e) => setNewVehicle({...newVehicle, stock: Number(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="1"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mileage</label>
                <input
                  type="number"
                  value={newVehicle.mileage}
                  onChange={(e) => setNewVehicle({...newVehicle, mileage: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="12000"
                  min="0"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleAddVehicle}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                  Add Vehicle
                </button>
                <button
                  onClick={() => setShowInventoryModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )
      }

      {showSaleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Add New Sale</h3>
              <button onClick={() => setShowSaleModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4">
              {/* Vehicle Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle</label>
                <select
                  value={newSale.vehicleId}
                  onChange={(e) => setNewSale({...newSale, vehicleId: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select a vehicle</option>
                  {vehicles.filter((v: any) => v.stock > 0).map((v: any) => (
                    <option key={v.id} value={v.id}>
                      {v.make} {v.model} {v.year} - £{v.price.toLocaleString()}
                    </option>
                  ))}
                </select>
              </div>

              {/* Customer Details */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">Customer Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
                    <input
                      type="text"
                      value={newSale.customer}
                      onChange={(e) => setNewSale({...newSale, customer: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={newSale.customerEmail}
                      onChange={(e) => setNewSale({...newSale, customerEmail: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="john@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={newSale.customerPhone}
                      onChange={(e) => setNewSale({...newSale, customerPhone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="07700 900123"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <textarea
                      value={newSale.customerAddress}
                      onChange={(e) => setNewSale({...newSale, customerAddress: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="123 High Street, London, SW1A 1AA"
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              {/* Sale Price & Sales Person */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sale Price</label>
                  <input
                    type="number"
                    value={newSale.salePrice}
                    onChange={(e) => setNewSale({...newSale, salePrice: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="25000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sales Person</label>
                  <input
                    type="text"
                    value={newSale.salesPerson}
                    onChange={(e) => setNewSale({...newSale, salesPerson: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Mike Johnson"
                  />
                </div>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={newSale.date}
                  onChange={(e) => setNewSale({...newSale, date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Fees Section */}
              {/* Documentation Fee and Registration Fee fields removed as requested */}
              {vatRegistered && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">VAT on Sale (£)</label>
                  <input type="number" value={newSale.vat} onChange={e => setNewSale({...newSale, vat: String(e.target.value)})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder={`Calculated: £${((parseFloat(newSale.salePrice as string || '0') + Number(newSale.documentationFee) + Number(newSale.registrationFee)) * vatRate).toFixed(2)}`} />
                  <small className="text-gray-500">Leave blank to auto-calculate</small>
                </div>
              )}

              {/* Extras Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Extras</label>
                {newSale.extras.map((extra, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <input type="text" value={extra.name} onChange={e => {
                      const updated = [...newSale.extras];
                      updated[idx].name = e.target.value;
                      setNewSale({...newSale, extras: updated});
                    }} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg" placeholder="Extra name" />
                    <input type="number" value={extra.price} onChange={e => {
                      const updated = [...newSale.extras];
                      updated[idx].price = Number(e.target.value);
                      setNewSale({...newSale, extras: updated});
                    }} className="w-24 px-3 py-2 border border-gray-300 rounded-lg" placeholder="Price" />
                    <button type="button" onClick={() => {
                      const updated = newSale.extras.filter((_, i) => i !== idx);
                      setNewSale({...newSale, extras: updated});
                    }} className="text-red-600">✕</button>
                  </div>
                ))}
                <button type="button" onClick={() => setNewSale({...newSale, extras: [...newSale.extras, { name: '', price: 0 }]})} className="text-blue-600 hover:underline">+ Add Extra</button>
              </div>
              {/* Remove blank extras before saving */}
              <input type="hidden" value="" onChange={() => {}} onBlur={() => {
                setNewSale({
                  ...newSale,
                  extras: newSale.extras.filter(extra => extra.name && extra.name.trim() !== '')
                });
              }} />

              {/* Trade-In & Discounts Section */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trade-In Description</label>
                  <input type="text" value={newSale.tradeInDescription} onChange={e => setNewSale({...newSale, tradeInDescription: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Description" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trade-In Value (£)</label>
                  <input type="number" value={newSale.tradeInValue} onChange={e => setNewSale({...newSale, tradeInValue: Number(e.target.value)})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="-£0" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Discount Note</label>
                  <input type="text" value={newSale.discountNote} onChange={e => setNewSale({...newSale, discountNote: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Note" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Discount Value (£)</label>
                  <input type="number" value={newSale.discountValue} onChange={e => setNewSale({...newSale, discountValue: Number(e.target.value)})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="-£0" />
                </div>
              </div>

              {/* Payment & Finance Section */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                  <select value={newSale.paymentMethod} onChange={e => setNewSale({...newSale, paymentMethod: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    <option>Bank Transfer</option>
                    <option>Card</option>
                    <option>Cash</option>
                    <option>Finance</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deposit (£)</label>
                  <input type="number" value={newSale.deposit} onChange={e => setNewSale({...newSale, deposit: Number(e.target.value)})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="£0" />
                </div>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <input type="checkbox" id="useFinance" checked={newSale.useFinance} onChange={e => setNewSale({...newSale, useFinance: e.target.checked})} className="w-4 h-4" />
                <label htmlFor="useFinance" className="text-sm font-medium text-gray-700">Use Finance</label>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">APR (%)</label>
                  <input type="number" value={newSale.apr} onChange={e => setNewSale({...newSale, apr: Number(e.target.value)})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="8.9" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Term (months)</label>
                  <input type="number" value={newSale.termMonths} onChange={e => setNewSale({...newSale, termMonths: Number(e.target.value)})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="36" />
                </div>
              </div>

              {/* Notes & Terms Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={newSale.notes} onChange={e => setNewSale({...newSale, notes: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" rows={2} placeholder="All vehicles HPI-clear. Subject to terms & conditions." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions</label>
                <textarea value={newSale.terms} onChange={e => setNewSale({...newSale, terms: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" rows={2} placeholder="Terms & Conditions" />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleAddSale}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                  Add Sale & Generate Invoice
                </button>
                <button
                  onClick={() => setShowSaleModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showExpenseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Add New Expense</h3>
              <button onClick={() => setShowExpenseModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="flex items-center gap-2 mb-3">
                  <input
                    type="checkbox"
                    checked={newExpense.linkedToStock}
                    onChange={(e) => setNewExpense({
                      ...newExpense, 
                      linkedToStock: e.target.checked,
                      category: e.target.checked ? 'Fuel' : 'Rent'
                    })}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Link to Vehicle (incremental cost)</span>
                </label>
              </div>

              {newExpense.linkedToStock && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Vehicle</label>
                  <select
                    value={newExpense.vehicleId}
                    onChange={(e) => setNewExpense({...newExpense, vehicleId: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select a vehicle</option>
                    {vehicles.map((v: any) => (
                      <option key={v.id} value={v.id}>
                        {v.make} {v.model} {v.year} - Current Add Cost: £{v.additionalCosts.toLocaleString()}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={newExpense.category}
                  onChange={(e) => setNewExpense({...newExpense, category: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {newExpense.linkedToStock ? (
                    stockExpenseCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))
                  ) : (
                    generalExpenseCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                <input
                  type="number"
                  value={newExpense.amount}
                  onChange={(e) => setNewExpense({...newExpense, amount: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="1500"
                />
              </div>
              {vatRegistered && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">VAT on Expense (£)</label>
                  <input type="number" value={newExpense.vat} onChange={e => setNewExpense({...newExpense, vat: String(e.target.value)})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder={`Calculated: £${(parseFloat(newExpense.amount as string || '0') * vatRate).toFixed(2)}`} />
                  <small className="text-gray-500">Leave blank to auto-calculate</small>
                </div>
              )}

              <div>

                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newExpense.description}
                  onChange={(e) => setNewExpense({...newExpense, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter expense details"
                  rows={3}
                />
              </div>

              {/* Invoice Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload Invoice (PDF, JPG, PNG)</label>
                <input
                  type="file"
                  accept=".pdf,image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (ev) => {
                        setNewExpense({ ...newExpense, invoiceFileUrl: ev.target?.result as string });
                      };
                      reader.readAsDataURL(file);
                    } else {
                      setNewExpense({ ...newExpense, invoiceFileUrl: '' });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {newExpense.invoiceFileUrl && (
                  <span className="text-green-600 text-xs">Invoice attached</span>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={newExpense.date}
                  onChange={(e) => setNewExpense({...newExpense, date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleAddExpense}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                  Add Expense
                </button>
                <button
                  onClick={() => setShowExpenseModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showInvoiceModal && selectedSale && (
        editingInvoice ? (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-screen overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900">Edit Invoice</h3>
                <button onClick={() => setEditingInvoice(false)} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
              </div>
              <div className="space-y-4">
                {/* Customer Details */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Customer Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
                      <input type="text" value={selectedSale.customer} onChange={e => setSelectedSale({...selectedSale, customer: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input type="email" value={selectedSale.customerEmail} onChange={e => setSelectedSale({...selectedSale, customerEmail: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input type="tel" value={selectedSale.customerPhone} onChange={e => setSelectedSale({...selectedSale, customerPhone: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                      <textarea value={selectedSale.customerAddress} onChange={e => setSelectedSale({...selectedSale, customerAddress: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" rows={2} />
                    </div>
                  </div>
                </div>
                {/* Sale Price & Sales Person */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sale Price</label>
                    <input type="number" value={selectedSale.salePrice} onChange={e => setSelectedSale({...selectedSale, salePrice: Number(e.target.value)})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sales Person</label>
                    <input type="text" value={selectedSale.salesPerson} onChange={e => setSelectedSale({...selectedSale, salesPerson: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  </div>
                </div>
                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input type="date" value={selectedSale.date} onChange={e => setSelectedSale({...selectedSale, date: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                {/* Fees Section */}
                {/* Documentation Fee and Registration Fee fields removed as requested */}
                {/* Payment & Finance Section */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                    <select value={selectedSale.paymentMethod} onChange={e => setSelectedSale({...selectedSale, paymentMethod: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                      <option>Bank Transfer</option>
                      <option>Card</option>
                      <option>Cash</option>
                      <option>Finance</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Deposit (£)</label>
                    <input type="number" value={selectedSale.deposit} onChange={e => setSelectedSale({...selectedSale, deposit: Number(e.target.value)})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  </div>
                </div>
                {/* Notes & Terms Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea value={selectedSale.notes} onChange={e => setSelectedSale({...selectedSale, notes: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" rows={2} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions</label>
                  <textarea value={selectedSale.terms} onChange={e => setSelectedSale({...selectedSale, terms: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" rows={2} />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => {
                      // Save changes to sales array and localStorage
                      setSales(sales.map(s => s.id === selectedSale.id ? selectedSale : s));
                      saveLS('sales', sales.map(s => s.id === selectedSale.id ? selectedSale : s));
                      setEditingInvoice(false);
                    }}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                  >
                    Save Changes
                  </button>
                  <button
                    onClick={() => setEditingInvoice(false)}
                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 print:static print:bg-transparent print:p-0">
          <div className="bg-white rounded-2xl max-w-3xl w-full p-10 shadow-2xl border border-gray-200 max-h-screen overflow-y-auto invoice-modal-print print:max-w-none print:w-full print:rounded-none print:p-0 print:shadow-none print:overflow-visible print:max-h-none print:border-0 print:box-border print:m-0 print:relative">
            {/* Modern Header with Logo and Dealership Info (matches PDF) */}
            <div className="flex items-center justify-between mb-8 print:flex-col print:items-start print:gap-2">
              <div className="flex items-center gap-4">
                {/* Only show logo if it loads, no icon/circle fallback */}
                <img
                  src={currentDealer.logo || logo}
                  alt={`${currentDealer.businessName} Logo`}
                  style={{ maxHeight: '64px', width: 'auto', display: 'block' }}
                  onError={e => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <div>
                  <h1 className="text-3xl font-extrabold text-blue-700 tracking-tight print:text-2xl print:mb-1">{currentDealer.businessName}</h1>
                  <p className="text-gray-500 font-medium">{currentDealer.address}</p>
                  <p className="text-gray-500">Phone: {currentDealer.phone}</p>
                  {currentDealer.email && <p className="text-gray-500">Email: {currentDealer.email}</p>}
                  {currentDealer.website && <p className="text-gray-500">Website: {currentDealer.website}</p>}
                </div>
              </div>
              <div className="text-right">
                <h2 className="text-2xl font-bold text-gray-900 print:text-xl">SALES INVOICE</h2>
                <p className="text-blue-600 font-semibold mt-1 print:mt-0">{selectedSale.invoiceNumber}</p>
                <p className="text-gray-600">Date: {new Date(selectedSale.date).toLocaleDateString('en-GB')}</p>
              </div>
              <button onClick={() => setShowInvoiceModal(false)} className="text-gray-400 hover:text-gray-600 print:hidden">
                <X size={28} />
              </button>
            </div>
                        {/* Bill To & Details Section (matches PDF) */}
            <div className="grid grid-cols-2 gap-8 mb-8 print:gap-4">
              <div className="bg-blue-50 rounded-lg p-6 border border-blue-100">
                <h3 className="font-semibold text-blue-700 mb-2 print:mb-1 print:text-lg">Bill To</h3>
                <p className="text-gray-900 font-bold print:text-base">{selectedSale.customer}</p>
                {/* Address lines wrap like PDF */}
                {selectedSale.customerAddress.split('\n').map((line, idx) => (
                  <p key={idx} className="text-gray-700 print:text-sm">{line}</p>
                ))}
                <p className="text-gray-700 mt-2 print:mt-0 print:text-sm">Email: {selectedSale.customerEmail}</p>
                <p className="text-gray-700 print:text-sm">Phone: {selectedSale.customerPhone}</p>
              </div>
              <div className="bg-white rounded-lg p-6 border border-gray-100">
                <h3 className="font-semibold text-gray-700 mb-2 print:mb-1 print:text-lg">Details</h3>
                <div className="mb-2 text-gray-600"><span className="font-medium">Sales Person:</span> {selectedSale.salesPerson}</div>
                <div className="mb-2 text-gray-600"><span className="font-medium">Payment Method:</span> {selectedSale.paymentMethod}</div>
                <div className="mb-2 text-gray-600"><span className="font-medium">Deposit:</span> £{selectedSale.deposit}</div>
              </div>
            </div>

        {/* Item Table (matches PDF) */}
            <div className="rounded-xl overflow-hidden border border-gray-200 shadow mb-8">
              <table className="w-full text-sm">
                <thead className="bg-blue-50">
                  <tr>
                    <th className="px-6 py-4 text-left font-bold text-blue-700">Item Description</th>
                    <th className="px-6 py-4 text-right font-bold text-blue-700">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {/* Main vehicle row */}
                  <tr className="bg-white">
                    <td className="px-6 py-4">
                      <div className="font-semibold text-gray-900">{selectedSale.vehicle}</div>
                      <div className="text-xs text-gray-500">VRN: {vehicles.find((v: any) => v.id === selectedSale.vehicleId)?.vin}</div>
                      <div className="text-xs text-gray-500">Mileage: {vehicles.find((v: any) => v.id === selectedSale.vehicleId)?.mileage?.toLocaleString()} mi</div>
                    </td>
                    <td className="px-6 py-4 text-right font-semibold text-gray-900">
                      £{selectedSale.salePrice.toLocaleString()}
                    </td>
                  </tr>
                  {/* Fees Section */}
                  {/* Documentation Fee and Registration Fee removed as requested */}
                  {/* Extras Section */}
                  {selectedSale.extras && selectedSale.extras.length > 0 && selectedSale.extras.filter(extra => extra.name && extra.name.trim() !== '').map((extra, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="px-6 py-4 font-medium text-gray-700">{extra.name}</td>
                      <td className="px-6 py-4 text-right font-medium text-gray-700">£{Number(extra.price || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                  {/* Trade-In & Discount Section */}
                  {Number(selectedSale.tradeInValue) > 0 && (
                    <tr className="bg-white">
                      <td className="px-6 py-4 font-medium text-red-600">Trade-In</td>
                      <td className="px-6 py-4 text-right font-medium text-red-600">-£{selectedSale.tradeInValue}</td>
                    </tr>
                  )}
                  {Number(selectedSale.discountValue) > 0 && (
                    <tr className="bg-white">
                      <td className="px-6 py-4 font-medium text-red-600">Discount</td>
                      <td className="px-6 py-4 text-right font-medium text-red-600">-£{selectedSale.discountValue}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Totals Section (matches PDF) */}
            <div className="flex justify-end mb-8">
              <div className="w-80 bg-blue-50 rounded-xl shadow-lg p-6 border border-blue-200">
                <div className="flex justify-between py-2 text-blue-700 font-semibold">
                  <span>Subtotal</span>
                  <span>£{selectedSale.salePrice.toLocaleString()}</span>
                </div>
                {/* Documentation Fee and Registration Fee removed as requested */}
                {selectedSale.extras && selectedSale.extras.length > 0 &&
                  selectedSale.extras.filter(extra => extra.name && extra.name.trim() !== '').map((extra, idx) => (
                    <div key={idx} className="flex justify-between py-2 text-blue-700">
                      <span>{extra.name}</span>
                      <span>£{Number(extra.price || 0).toLocaleString()}</span>
                    </div>
                  ))}
                {selectedSale.tradeInValue && selectedSale.tradeInValue > 0 && (
                  <div className="flex justify-between py-2 text-red-600">
                    <span>Trade-In</span>
                    <span>-£{selectedSale.tradeInValue}</span>
                  </div>
                )}
                {selectedSale.discountValue && selectedSale.discountValue > 0 && (
                  <div className="flex justify-between py-2 text-red-600">
                    <span>Discount</span>
                    <span>-£{selectedSale.discountValue}</span>
                  </div>
                )}
                <div className="flex justify-between py-3 border-t border-blue-300 text-xl font-bold text-blue-900 mt-2">
                  <span>Total</span>
                  <span>
                    £{
                      (
                        selectedSale.salePrice +
                        (selectedSale.extras ? selectedSale.extras.reduce((sum, e) => sum + (e.price || 0), 0) : 0)
                        - (selectedSale.tradeInValue || 0)
                        - (selectedSale.discountValue || 0)
                      ).toLocaleString()
                    }
                  </span>
                </div>
              </div>
            </div>

            {/* Payment & Finance Section (matches PDF) */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold mb-2 text-blue-700">Payment & Finance</h4>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-gray-700 text-sm">
                <div className="mb-2">Payment Method: {selectedSale.paymentMethod}</div>
                <div className="mb-2">Deposit: £{selectedSale.deposit}</div>
                {selectedSale.useFinance && (
                  <>
                    <div className="mb-2">Finance: Yes</div>
                    <div className="mb-2">APR: {selectedSale.apr}%</div>
                    <div className="mb-2">Term: {selectedSale.termMonths} months</div>
                  </>
                )}
              </div>
            </div>

            {/* Notes & Terms Section (matches PDF) */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold mb-2 text-blue-700">Notes</h4>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-gray-700 text-sm">
                {selectedSale.notes || 'All vehicles HPI-clear. Subject to terms & conditions.'}
              </div>
            </div>
            <div className="mb-8">
              <h4 className="text-lg font-semibold mb-2 text-blue-700">Terms & Conditions</h4>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-gray-700 text-xs whitespace-pre-line">
                {selectedSale.terms || `TERMS & CONDITIONS OF SALE\n\n1. Definitions\nSeller means HNS Cars. Customer/Buyer means the person purchasing the vehicle. Vehicle means the used motor vehicle described in this invoice.\n\n2. Condition of Vehicle\nThe Vehicle is sold as a used vehicle; age-related wear is to be expected. No representation is made beyond information provided in good faith unless expressly stated in writing.\n\n3. Inspection & Test Drive\nCustomer has had the opportunity to inspect/test-drive and accepts the Vehicle's condition.\n\n4. Price & Payment\nDeposit (if any) is non-refundable unless the Seller cannot supply the Vehicle. Full cleared funds required before release.\n\n5. Title & Risk\nTitle passes on receipt of full cleared payment. Risk passes on collection/delivery.\n\n6. Collection & Delivery\nUnless agreed in writing, Customer collects from Seller's premises. Any delivery is at Customer's cost and risk.\n\n7. Warranty\nWhere a warranty is provided, its duration/scope/exclusions are in a separate schedule. Warranty Claim – Customer Responsibility: in the event of a warranty claim, the Customer is responsible for transporting the Vehicle to the Seller's designated yard/service location for inspection/repair; all transport costs/risks are borne by the Customer unless otherwise agreed in writing. Normal wear/consumables/misuse/accident damage excluded.\n\n8. Part-Exchange (if applicable)\nCustomer warrants full title and disclosure of known defects; allowance may be adjusted if undisclosed issues are found.\n\n9. Finance\nCustomer is responsible for complying with any finance agreement terms.\n\n10. Limitation of Liability\nNothing limits liability for death/personal injury due to negligence or fraud. Seller not liable for indirect/consequential losses (loss of profit/downtime).\n\n11. Data Protection\nCustomer data processed to complete the sale and provide after-sales support in accordance with applicable law.\n\n12. Governing Law\nThese terms are governed by the laws of England & Wales and subject to the exclusive jurisdiction of the English courts.`}
              </div>
            </div>

            {/* Signature & Thank You Section (matches PDF) */}
            <div className="mt-8 pt-6 border-t border-gray-200 text-center text-base text-blue-700 font-semibold">
              <p>Customer Signature: ___________________________</p>
              <p>Date: _______________</p>
              <p className="mt-4">Thank you for your business!</p>
            </div>

            <div className="flex gap-3 mt-6 print:hidden">
              <button
                onClick={() => setEditingInvoice(true)}
                className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 font-medium flex items-center justify-center gap-2 shadow"
              >
                Edit Invoice
              </button>
              <button
                onClick={() => {
                  const modal = document.querySelector('.invoice-modal-print');
                  if (!modal) return window.print();
                  const printContents = modal.innerHTML;
                  const printWindow = window.open('', '', 'height=900,width=800');
                  if (printWindow) {
                    printWindow.document.write('<html><head><title>Invoice</title>');
                    Array.from(document.querySelectorAll('link[rel="stylesheet"]')).forEach(linkTag => {
                      printWindow.document.write(linkTag.outerHTML);
                    });
                    Array.from(document.querySelectorAll('style')).forEach(styleTag => {
                      printWindow.document.write(`<style>${styleTag.innerHTML}</style>`);
                    });
                    if (document.styleSheets) {
                      try {
                        for (let sheet of Array.from(document.styleSheets)) {
                          if (sheet.cssRules) {
                            let css = '';
                            for (let rule of Array.from(sheet.cssRules)) {
                              css += rule.cssText + '\n';
                            }
                            printWindow.document.write(`<style>${css}</style>`);
                          }
                        }
                      } catch (e) { /* ignore cross-origin errors */ }
                    }
                    printWindow.document.write('</head><body style="margin:0">');
                    printWindow.document.write(printContents);
                    printWindow.document.write('</body></html>');
                    printWindow.document.close();
                    printWindow.focus();
                    setTimeout(() => {
                      printWindow.print();
                      printWindow.close();
                    }, 500);
                  }
                }}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center gap-2 shadow"
              >
                <Download size={18} />
                Print Invoice
              </button>
              <button
                onClick={() => generateInvoicePDF(selectedSale, vehicles, currentDealer)}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center justify-center gap-2 shadow"
              >
                <Download size={18} />
                Download PDF
              </button>
              <button
                onClick={() => setShowInvoiceModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium shadow"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* Dealer Management Modal */}
      {showDealerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-6 max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Manage Dealers</h3>
              <button onClick={() => setShowDealerModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Current Dealers</h4>
                <div className="space-y-2">
                  {dealers.map(dealer => (
                    <div key={dealer.id} className="flex items-center justify-between p-2 border rounded">
                      <div>
                        <div className="font-medium">{dealer.name}</div>
                        <div className="text-sm text-gray-600">{dealer.businessName}</div>
                      </div>
                      <div className="flex gap-2">
                        {dealer.id === currentDealerId && (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                            Current
                          </span>
                        )}
                        <button
                          onClick={() => switchToDealer(dealer.id)}
                          className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded hover:bg-blue-200"
                          disabled={dealer.id === currentDealerId}
                        >
                          Select
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Add New Dealer</h4>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      placeholder="Dealer Name"
                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      id="newDealerName"
                    />
                    <input
                      type="text"
                      placeholder="Business Name"
                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      id="newDealerBusinessName"
                    />
                  </div>
                  <textarea
                    placeholder="Address"
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    id="newDealerAddress"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      placeholder="Phone"
                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      id="newDealerPhone"
                    />
                    <input
                      type="email"
                      placeholder="Email"
                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      id="newDealerEmail"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      placeholder="Website (optional)"
                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      id="newDealerWebsite"
                    />
                    <input
                      type="text"
                      placeholder="License Number (optional)"
                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      id="newDealerLicense"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL (optional)</label>
                    <input
                      type="url"
                      placeholder="https://example.com/logo.png"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      id="newDealerLogo"
                    />
                    <p className="text-xs text-gray-500 mt-1">Enter the URL of your logo image (PNG, JPG, SVG)</p>
                  </div>
                  <button
                    onClick={() => {
                      const name = (document.getElementById('newDealerName') as HTMLInputElement).value;
                      const businessName = (document.getElementById('newDealerBusinessName') as HTMLInputElement).value;
                      const address = (document.getElementById('newDealerAddress') as HTMLTextAreaElement).value;
                      const phone = (document.getElementById('newDealerPhone') as HTMLInputElement).value;
                      const email = (document.getElementById('newDealerEmail') as HTMLInputElement).value;
                      const website = (document.getElementById('newDealerWebsite') as HTMLInputElement).value;
                      const licenseNumber = (document.getElementById('newDealerLicense') as HTMLInputElement).value;
                      const logoUrl = (document.getElementById('newDealerLogo') as HTMLInputElement).value;
                      
                      if (name && businessName && address && phone && email) {
                        const newDealer: Dealer = {
                          id: Math.max(...dealers.map(d => d.id)) + 1,
                          name,
                          businessName,
                          address,
                          phone,
                          email,
                          website: website || undefined,
                          licenseNumber: licenseNumber || undefined,
                          logo: logoUrl || undefined,
                          vatNumber: ''
                        };
                        setDealers([...dealers, newDealer]);
                        
                        // Clear form
                        (document.getElementById('newDealerName') as HTMLInputElement).value = '';
                        (document.getElementById('newDealerBusinessName') as HTMLInputElement).value = '';
                        (document.getElementById('newDealerAddress') as HTMLTextAreaElement).value = '';
                        (document.getElementById('newDealerPhone') as HTMLInputElement).value = '';
                        (document.getElementById('newDealerEmail') as HTMLInputElement).value = '';
                        (document.getElementById('newDealerWebsite') as HTMLInputElement).value = '';
                        (document.getElementById('newDealerLicense') as HTMLInputElement).value = '';
                        (document.getElementById('newDealerLogo') as HTMLInputElement).value = '';
                      }
                    }}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium"
                  >
                    Add Dealer
                  </button>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowDealerModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VehicleStockApp;